import { Box, Loader } from "@saviynt/design-system";
import {
  createRootRoute,
  Outlet,
  redirect,
  useNavigate,
  useRouterState,
} from "@tanstack/react-router";
import PageNotFound from "../components/PageNotFound";
import { TanStackRouterDevtools } from "@tanstack/react-router-devtools";
import { useEffect, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { ShellLayout } from "../shell-components/ShellLayout";
import { ViewportProvider } from "../contexts/ViewportContext";
import SSOCallbackPage from "../pages/SSOCallback/SSOCallbackPage";
import { hasValidSession } from "../utils/auth";
import "../utils/devtools";

function RootComponent() {
  const [showRouterDevtools, setShowRouterDevtools] = useState(false);
  const { isAuthenticated, isAuthChecked, getHomeRoute, syncFromStorage } =
    useAuth();
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  useEffect(() => {
    const handleToggle = (event: CustomEvent) => {
      setShowRouterDevtools(event.detail);
    };
    window.addEventListener(
      "devtools-router-toggle",
      handleToggle as EventListener,
    );
    return () =>
      window.removeEventListener(
        "devtools-router-toggle",
        handleToggle as EventListener,
      );
  }, []);

  useEffect(() => {
    if (!isAuthChecked) return;
    if (pathname === "/version" || pathname === "/sso/callback") return;

    const sessionOk = hasValidSession();

    // Tokens exist but React auth was cleared without clearing storage.
    // Re-sync instead of sending users to /login (that loops with login
    // beforeLoad → `/` and leaves a blank page). Skip /login so MFA
    // SuccessStep can keep tokens without flipping auth yet.
    if (!isAuthenticated && sessionOk && pathname !== "/login") {
      syncFromStorage();
      return;
    }

    // Truly logged out → login.
    if (!isAuthenticated && !sessionOk && pathname !== "/login") {
      void navigate({ to: "/login" });
      return;
    }

    // Logged in while still on /login (same-tab success or cross-tab login).
    if (isAuthenticated && sessionOk && pathname === "/login") {
      void navigate({ to: getHomeRoute() });
    }
  }, [
    isAuthenticated,
    isAuthChecked,
    pathname,
    navigate,
    getHomeRoute,
    syncFromStorage,
  ]);

  if (pathname === "/sso/callback") return <SSOCallbackPage />;
  if (pathname === "/version") return <Outlet />;

  if (!isAuthChecked) {
    return (
      <Box className="w-full h-screen flex justify-center items-center">
        <Loader kind="loop" format="main" size="medium" color="brand" />
      </Box>
    );
  }

  // Never wrap /login in ShellLayout. Cross-tab login flips isAuthenticated
  // before navigation leaves /login; wrapping would mash Shell + Login UI.
  // Also keep bare Outlet while session tokens exist but React auth is still
  // catching up on `/` — syncFromStorage above heals that on the next tick.
  //
  // Wrap in ViewportProvider so route components that call useViewport (the MFE
  // routes: /agents, /admin/audit-trail) don't crash if isAuthenticated flips to
  // false while one is mounted — e.g. a mid-session token-refresh failure fires
  // `auth:unauthorized` and this branch re-renders the same route before the
  // redirect-to-login effect runs. ShellLayout supplies its own provider in the
  // authenticated branch, so the two never nest.
  if (!isAuthenticated || pathname === "/login") {
    return (
      <ViewportProvider>
        <Outlet />
      </ViewportProvider>
    );
  }

  return (
    <>
      <ShellLayout>
        <Outlet />
      </ShellLayout>
      {showRouterDevtools && <TanStackRouterDevtools />}
    </>
  );
}

export const Route = createRootRoute({
  notFoundComponent: PageNotFound,
  beforeLoad: ({ location }) => {
    if (location.pathname.startsWith("/ECMv6")) {
      const stripped = location.pathname.replace(/^\/ECMv6/, "") || "/";
      throw redirect({
        to: stripped,
        search: location.search,
        hash: location.hash,
        replace: true,
      });
    }
  },
  component: RootComponent,
});
